// Renders a post or comment body: text, inline GIFs and images, and links.
//
// Bodies are stored and rendered as plain text — React escapes them, and that
// is what keeps a post from injecting markup. This keeps that property: it
// never sets HTML, it splits the text and returns React elements, so anything
// that is not recognised stays literal text.
//
// A GIF is shared by putting its URL in the message. That works with every
// source — Giphy, Tenor, a direct upload — without an API key or a third-party
// script, and it is why there is no picker here: a search picker needs a
// provider key that only the account owner can issue.

import React from 'react';
import styled from 'styled-components';

// Matched against the path, so query strings do not defeat it (Giphy and
// Tenor both append them).
const IMAGE_EXTENSIONS = /\.(gif|gifv|png|jpe?g|webp|avif)$/i;

// Hosts whose share links point at a GIF even without an image extension.
const GIF_HOSTS = ['giphy.com', 'media.giphy.com', 'tenor.com', 'media.tenor.com', 'c.tenor.com'];

const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/gi;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"»]+$/;

const Media = styled.img`
  display: block;
  max-width: min(320px, 100%);
  max-height: 320px;
  width: auto;
  height: auto;
  border-radius: 12px;
  margin: 0.5rem 0;
  object-fit: contain;
  background: rgba(127, 127, 127, 0.12);
`;

const Anchor = styled.a`
  color: #ff8c42;
  text-decoration: underline;
  word-break: break-word;
`;

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** True when the URL should render as an image rather than a link. */
function isRenderableMedia(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  // http(s) only. A data: or javascript: URL must never reach an img src, and
  // the URL pattern above already excludes them — this is the belt to that
  // pattern's braces.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;

  if (IMAGE_EXTENSIONS.test(parsed.pathname)) return true;

  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  // Only the media paths, not a Giphy article or a Tenor profile page.
  return GIF_HOSTS.includes(host) && /\/(media|gifs?|view)\//i.test(parsed.pathname);
}

interface RichBodyProps {
  text: string;
  className?: string;
}

export const RichBody: React.FC<RichBodyProps> = ({ text, className }) => {
  if (!text) return null;

  const parts = text.split(URL_PATTERN);

  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (!part) return null;

        // Odd indices are the captured URLs; even indices are literal text.
        if (index % 2 === 0) return <React.Fragment key={index}>{part}</React.Fragment>;

        const trailing = part.match(TRAILING_PUNCTUATION)?.[0] ?? '';
        const url = trailing ? part.slice(0, -trailing.length) : part;

        if (isRenderableMedia(url)) {
          return (
            <React.Fragment key={index}>
              <Media src={url} alt="Shared GIF" loading="lazy" />
              {trailing}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={index}>
            {/* noopener/noreferrer so a linked page cannot reach back through
                window.opener; nofollow because these are user submissions. */}
            <Anchor href={url} target="_blank" rel="noopener noreferrer nofollow">
              {url}
            </Anchor>
            {trailing}
          </React.Fragment>
        );
      })}
    </span>
  );
};
